import { Button, Card, Collapse } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { useTelegram } from '../../context/TelegramContext';
import { giftsApi } from '../../api/gifts';
import { materialFileUrl, materialsApi } from '../../api/materials';
import { referralApi } from '../../api/referral';
import { downloadFile, openGiftLink } from '../../utils/telegramLink';
import { GiftItem, GiftsPayload } from '../../types/gifts';
import { MaterialItem } from '../../types/materials';
import { ReferralLinkPayload } from '../../types/referral';
import VividProfileHeader from '../../components/vivid/VividProfileHeader';
import VividProgress from '../../components/vivid/VividProgress';
import GiftTimeline from '../../components/vivid/GiftTimeline';
import MaterialsSection from '../../components/vivid/MaterialsSection';
import ReferralSection from '../../components/vivid/ReferralSection';
import MaterialViewer from '../../components/vivid/MaterialViewer';
import ClassicSubscriptionPanel from '../../components/ClassicSubscriptionPanel';
import TributeSubscriptionPanel from '../../components/TributeSubscriptionPanel';

const UZDEVID_URL = 'https://t.me/uzdevid';

const VividDashboard = () => {
    const { webApp } = useTelegram();
    const { subscriptionStatus } = useOnboarding();
    const isTribute = subscriptionStatus?.source === 'tribute';

    const [payload, setPayload] = useState<GiftsPayload | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [claimingId, setClaimingId] = useState<string | null>(null);

    // Materials load on their own path with their own catch: a materials failure must never take down
    // the gift timeline, and vice versa.
    const [materials, setMaterials] = useState<MaterialItem[]>([]);
    const [viewing, setViewing] = useState<MaterialItem | null>(null);

    // Same isolation for the referral link: it is a side block, so a failed load just hides it.
    const [referral, setReferral] = useState<ReferralLinkPayload | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(false);

        try {
            setPayload(await giftsApi.getGifts());
        } catch {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMaterials = useCallback(async () => {
        try {
            setMaterials((await materialsApi.getMaterials()).items);
        } catch {
            setMaterials([]);
        }
    }, []);

    const loadReferral = useCallback(async () => {
        try {
            setReferral(await referralApi.getReferralLink());
        } catch {
            setReferral(null);
        }
    }, []);

    useEffect(() => {
        load();
        loadMaterials();
        loadReferral();
    }, [load, loadMaterials, loadReferral]);

    // House rule: record the event first, then act; the server is idempotent, so always POST without a
    // client-side dedup, and never let a failed record block the view/download.
    const handleView = (item: MaterialItem) => {
        materialsApi.recordEvent(item.id, 'view').catch(() => {});
        setViewing(item);
    };

    const handleDownload = (item: MaterialItem) => {
        materialsApi.recordEvent(item.id, 'download').catch(() => {});
        const url = materialFileUrl(item.file_path);
        if (url) {
            downloadFile(webApp, url, item.title);
            webApp?.HapticFeedback?.notificationOccurred?.('success');
        }
    };

    const handleClaim = async (item: GiftItem) => {
        try {
            setClaimingId(item.id);

            // Already claimed → just re-open the stored link; otherwise record the claim and take its link.
            const link = item.state === 'claimed' && item.link ? item.link : (await giftsApi.claimGift(item.id)).link;

            // Persist first, then open: if the open is blocked, the claim is still recorded locally.
            setPayload((prev) =>
                prev
                    ? {
                          ...prev,
                          items: prev.items.map((it) =>
                              it.id === item.id
                                  ? { ...it, state: 'claimed' as const, link, claimed_time: it.claimed_time ?? new Date().toISOString() }
                                  : it
                          ),
                      }
                    : prev
            );

            openGiftLink(webApp, link);
            webApp?.HapticFeedback?.notificationOccurred?.('success');
        } catch {
            webApp?.HapticFeedback?.notificationOccurred?.('error');
        } finally {
            setClaimingId(null);
        }
    };

    if (isLoading) {
        return null;
    }

    if (error || !payload) {
        return (
            <div className="vivid-shell">
                <Card className="onboarding-card" bordered={false}>
                    <p className="section-text" style={{ marginBottom: 16 }}>
                        Ma'lumotlarni yuklab bo'lmadi.
                    </p>
                    <div className="error-action">
                        <Button type="primary" block onClick={load}>
                            Qayta urinib ko'rish
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="vivid-shell">
            <VividProfileHeader payload={payload} />
            <VividProgress monthsPaid={payload.months_paid} totalMonths={payload.total_months} />
            <GiftTimeline items={payload.items} claimingId={claimingId} onClaim={handleClaim} />

            <ReferralSection payload={referral} />

            <MaterialsSection items={materials} onView={handleView} onDownload={handleDownload} />

            <MaterialViewer material={viewing} onClose={() => setViewing(null)} />

            <Collapse
                className="vivid-manage"
                bordered={false}
                items={[
                    {
                        key: 'manage',
                        label: 'Obunani boshqarish',
                        children: isTribute ? <TributeSubscriptionPanel /> : <ClassicSubscriptionPanel />,
                    },
                ]}
            />

            <div className="app-footer">
                powered by{' '}
                <a className="app-footer__link" href={UZDEVID_URL} target="_blank" rel="noopener noreferrer">
                    UzDevid
                </a>
            </div>
        </div>
    );
};

export default VividDashboard;
